import './ThrillingLocationsNpcsDrawer.css';
import type {
  ThrillingLocationsNpcManifestEntry,
  ThrillingLocationsVenue,
} from '../../services/host/ThrillingLocationsServiceModule';
import type {
  ThrillingLocationsLibraryState,
  ThrillingLocationsLibraryTab,
} from '../../hooks/useThrillingLocationsLibrary';

/**
 * ThrillingLocationsNpcsDrawer — 2026-06-10 §3.F for Thrilling Locations
 * VG35010 venue-staff NPCs (39 across 6 venues).
 *
 * 6-venue tab layout (casino / hotel / restaurant / train / boat / plane). Each
 * NPC carries a PARTIAL stat block (WIL/PER per the source — venue staff use the
 * Civilians Table; STR/DEX/INT render '—'), physical, weaknesses, idiosyncrasies,
 * interaction_modifiers (STRING values so the book's dual notation '0/+2'
 * survives), concept + verbatim background. Four NPCs lost their stat header to
 * marker OCR → an ocr_artifacts_note surfaces honestly.
 *
 * Mirrors VillainsDossierDrawer (incl. the Bug-1 `onClose` render-gate idiom +
 * the ScenarioRunner.drawerOnClose guard test).
 */

const TAB_DEFS: ReadonlyArray<{ key: ThrillingLocationsVenue; label: string }> = [
  { key: 'casino', label: 'Casino' },
  { key: 'hotel', label: 'Hotel' },
  { key: 'restaurant', label: 'Restaurant' },
  { key: 'train', label: 'Train' },
  { key: 'boat', label: 'Boat' },
  { key: 'plane', label: 'Plane' },
];

const STAT_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'STR', label: 'STR' },
  { key: 'DEX', label: 'DEX' },
  { key: 'WIL', label: 'WIL' },
  { key: 'PER', label: 'PER' },
  { key: 'INT', label: 'INT' },
];

const PHYSICAL_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'height', label: 'Height' },
  { key: 'weight', label: 'Weight' },
  { key: 'age', label: 'Age' },
  { key: 'appearance', label: 'Appearance' },
];

const INTERACTION_ORDER: ReadonlyArray<string> = [
  'Reaction',
  'Persuasion',
  'Seduction',
  'Interrogation',
  'Torture',
];

export interface ThrillingLocationsNpcsDrawerProps {
  npcs: ThrillingLocationsNpcManifestEntry[];
  pendingRemainingCount: number;
  state: ThrillingLocationsLibraryState;
  error?: string | null;
  activeTab: ThrillingLocationsLibraryTab;
  onTabChange: (tab: ThrillingLocationsLibraryTab) => void;
  selectedNpcId: string | null;
  selectedNpcData: Record<string, unknown> | null;
  selectedNpcState: ThrillingLocationsLibraryState;
  onSelectNpc: (id: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function ThrillingLocationsNpcDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const name = data['name'] as string | undefined;
  const manifestSection = data['manifest_section'] as string | undefined;
  const sourceMdLine = data['source_md_line'] as number | undefined;
  const venue = data['venue'] as string | undefined;
  const roleSection = data['role_section'] as string | undefined;
  const stats = (data['stats'] ?? {}) as Record<string, number | null>;
  const physical = (data['physical'] ?? {}) as Record<string, unknown>;
  const weaknesses = (data['weaknesses'] ?? []) as string[];
  const idiosyncrasies = data['idiosyncrasies'] as string | null | undefined;
  const interactionModifiers = (data['interaction_modifiers'] ?? null) as
    | Record<string, string>
    | null;
  const concept = data['concept'] as string | null | undefined;
  const background = data['background'] as string | null | undefined;
  const notes = data['notes'] as string | null | undefined;
  const ocrArtifactsNote = data['ocr_artifacts_note'] as string | null | undefined;

  const hasAnyStats = STAT_ORDER.some(
    (s) => stats[s.key] !== null && stats[s.key] !== undefined,
  );

  return (
    <div
      className="br-tl-npc-detail"
      data-testid={`${testidPrefix}-detail`}
    >
      <div className="br-tl-npc-detail-header">
        <div className="br-tl-npc-detail-name br-display">{name}</div>
        <div className="br-tl-npc-detail-citation br-label">
          §{manifestSection}
          {sourceMdLine !== undefined ? ` · MD L${sourceMdLine}` : ''}
          {venue !== undefined ? ` · ${venue}` : ''}
          {roleSection !== undefined ? ` · ${roleSection}` : ''}
        </div>
      </div>

      {concept && (
        <div className="br-tl-npc-detail-section">
          <div className="br-tl-npc-detail-section-label">Concept</div>
          <p className="br-tl-npc-detail-prose">{concept}</p>
        </div>
      )}

      {ocrArtifactsNote && (
        <div
          className="br-tl-npc-detail-warn"
          data-testid={`${testidPrefix}-detail-ocr-note`}
        >
          <span className="br-tl-npc-detail-warn-icon">⚠</span>
          <span className="br-tl-npc-detail-warn-text">{ocrArtifactsNote}</span>
        </div>
      )}

      <div className="br-tl-npc-detail-section">
        <div className="br-tl-npc-detail-section-label">Stats</div>
        {hasAnyStats ? (
          <dl className="br-tl-npc-detail-stats">
            {STAT_ORDER.map((s) => {
              const v = stats[s.key];
              return (
                <div key={s.key} className="br-tl-npc-detail-stat-tile">
                  <dt>{s.label}</dt>
                  <dd className="br-mono">{v === null || v === undefined ? '—' : v}</dd>
                </div>
              );
            })}
          </dl>
        ) : (
          <p className="br-label br-tl-npc-detail-stats-empty">
            Stat header not present in source (use the Civilians Table).
          </p>
        )}
      </div>

      <div className="br-tl-npc-detail-section">
        <div className="br-tl-npc-detail-section-label">Physical</div>
        <dl className="br-tl-npc-detail-physical">
          {PHYSICAL_ORDER.map((p) => {
            const v = physical[p.key];
            return (
              <div key={p.key} className="br-tl-npc-detail-physical-row">
                <dt>{p.label}</dt>
                <dd>{v === null || v === undefined ? '—' : String(v)}</dd>
              </div>
            );
          })}
        </dl>
      </div>

      {weaknesses.length > 0 && (
        <div className="br-tl-npc-detail-section">
          <div className="br-tl-npc-detail-section-label">Weaknesses</div>
          <ul className="br-tl-npc-detail-chips">
            {weaknesses.map((w) => (
              <li key={w} className="br-tl-npc-detail-chip">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {idiosyncrasies && (
        <div className="br-tl-npc-detail-section">
          <div className="br-tl-npc-detail-section-label">Idiosyncrasies</div>
          <p className="br-tl-npc-detail-prose">{idiosyncrasies}</p>
        </div>
      )}

      {interactionModifiers && (
        <div className="br-tl-npc-detail-section">
          <div className="br-tl-npc-detail-section-label">
            Interaction Modifiers
          </div>
          <dl className="br-tl-npc-detail-interaction">
            {INTERACTION_ORDER.map((k) =>
              interactionModifiers[k] !== undefined ? (
                <div key={k} className="br-tl-npc-detail-interaction-row">
                  <dt>{k}</dt>
                  <dd className="br-mono">{interactionModifiers[k]}</dd>
                </div>
              ) : null,
            )}
          </dl>
        </div>
      )}

      {background && (
        <div className="br-tl-npc-detail-section">
          <div className="br-tl-npc-detail-section-label">Background</div>
          <p
            className="br-tl-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-background`}
          >
            {background}
          </p>
        </div>
      )}

      {notes && (
        <div className="br-tl-npc-detail-section">
          <div className="br-tl-npc-detail-section-label">Notes</div>
          <p
            className="br-tl-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-notes`}
          >
            {notes}
          </p>
        </div>
      )}
    </div>
  );
}

export function ThrillingLocationsNpcsDrawer({
  npcs,
  pendingRemainingCount,
  state,
  error,
  activeTab,
  onTabChange,
  selectedNpcId,
  selectedNpcData,
  selectedNpcState,
  onSelectNpc,
  onClose,
  testidPrefix = 'br-thrilling-locations-npcs-library',
}: ThrillingLocationsNpcsDrawerProps): JSX.Element {
  const visibleNpcs = npcs.filter((n) => n.venue === activeTab);

  return (
    <aside
      className="br-scenario-drawer br-thrilling-locations-npcs-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
    >
      <header>
        <h2 className="br-display">Thrilling Locations — Venue Staff</h2>
        {/* 2026-06-09 Bug 1 drift fix — render-gate the close button on
         *  `onClose !== undefined`, mirroring the 9 sibling library drawers +
         *  the ScenarioRunner.drawerOnClose guard test. */}
        {onClose !== undefined && (
          <button
            type="button"
            className="br-scenario-drawer-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close Thrilling Locations venue staff drawer"
          >
            ✕
          </button>
        )}
      </header>

      <p
        className="br-label br-tl-npc-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Victory Games VG35010 — Thrilling Locations (venue staff across casinos,
        hotels, restaurants, trains, boats &amp; planes).{' '}
        <strong>{npcs.length} authored</strong>
        {pendingRemainingCount > 0 ? (
          <> · {pendingRemainingCount} more enumerated as pending in the seed.</>
        ) : (
          <> · 0 pending.</>
        )}
      </p>

      <div
        className="br-scenario-drawer-tabs"
        data-testid={`${testidPrefix}-tabs`}
        role="tablist"
      >
        {TAB_DEFS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            data-testid={`${testidPrefix}-tab-${t.key}`}
            data-active={t.key === activeTab ? 'true' : 'false'}
            aria-selected={t.key === activeTab}
            onClick={() => onTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {state === 'loading' && (
        <div className="br-label" data-testid={`${testidPrefix}-loading`}>
          Loading venue staff…
        </div>
      )}
      {state === 'error' && (
        <div className="br-label" data-testid={`${testidPrefix}-error`}>
          {error ?? 'Failed to load Thrilling Locations venue staff.'}
        </div>
      )}
      {state === 'ready' && (
        <>
          <div data-testid={`${testidPrefix}-count`} className="br-label">
            {visibleNpcs.length}
          </div>
          <ul className="br-tl-npc-list" data-testid={`${testidPrefix}-list`}>
            {visibleNpcs.length === 0 && (
              <li
                className="br-label br-tl-npc-empty"
                data-testid={`${testidPrefix}-empty`}
              >
                No NPCs in this venue.
              </li>
            )}
            {visibleNpcs.map((n) => (
              <li key={n.id} className="br-tl-npc-row-wrapper">
                <button
                  type="button"
                  className="br-tl-npc-row"
                  data-testid={`${testidPrefix}-row-${n.id}`}
                  data-selected={n.id === selectedNpcId ? 'true' : 'false'}
                  onClick={() => onSelectNpc(n.id)}
                >
                  <span className="br-tl-npc-row-name">{n.name}</span>
                  <span className="br-tl-npc-row-section br-label">
                    §{n.manifest_section}
                  </span>
                </button>
                {n.id === selectedNpcId && selectedNpcState === 'loading' && (
                  <div
                    className="br-label"
                    data-testid={`${testidPrefix}-detail-loading`}
                  >
                    Loading detail…
                  </div>
                )}
                {n.id === selectedNpcId &&
                  selectedNpcState === 'ready' &&
                  selectedNpcData && (
                    <ThrillingLocationsNpcDetail
                      data={selectedNpcData}
                      testidPrefix={testidPrefix}
                    />
                  )}
                {n.id === selectedNpcId && selectedNpcState === 'error' && (
                  <div
                    className="br-label"
                    data-testid={`${testidPrefix}-detail-error`}
                  >
                    Failed to load detail.
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}
